"use client";

import { ArrowRight, Cpu, FileText, Activity, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TheOlfactoryQuiz() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const features = [{
    icon: Cpu,
    title: "OLFACTORY ANALYSIS",
    description: "Advanced molecular profiling to identify the unique character of your signature scent."
  }, {
    icon: Activity,
    title: "SENSORY MAPPING",
    description: "Real-time evaluation of top, heart, and base notes to ensure perfect harmony."
  }, {
    icon: Wrench,
    title: "ARTISAN CALIBRATION",
    description: "Master perfumer-grade techniques for precise, balanced, and long-lasting compositions."
  }, {
    icon: FileText,
    title: "CURATED PROFILES",
    description: "Detailed documentation of your fragrance journey with bespoke recommendations."
  }];
  return <section data-section-id="30" id="the-olfactory-quiz" className="dark bg-background text-foreground relative overflow-hidden py-24 md:py-40 px-4 md:px-8">
      {}
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
      backgroundImage: "linear-gradient(var(--foreground) 1px, transparent 1px), linear-gradient(90deg, var(--foreground) 1px, transparent 1px)",
      backgroundSize: "48px 48px"
    }} aria-hidden="true" />
      {}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
      backgroundImage: "repeating-linear-gradient(0deg, var(--foreground) 0px, var(--foreground) 1px, transparent 1px, transparent 3px)"
    }} aria-hidden="true" />

      <div className="relative max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {}
          <div ref={leftRef} className={`relative transition-all duration-700 ease-out ${leftVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
            <div className="relative">
              {}
              <div className="absolute -top-3 -left-3 w-6 h-6 border-t-2 border-l-2 border-primary z-10" aria-hidden="true" />
              <div className="absolute -top-3 -right-3 w-6 h-6 border-t-2 border-r-2 border-primary z-10" aria-hidden="true" />
              <div className="absolute -bottom-3 -left-3 w-6 h-6 border-b-2 border-l-2 border-primary z-10" aria-hidden="true" />
              <div className="absolute -bottom-3 -right-3 w-6 h-6 border-b-2 border-r-2 border-primary z-10" aria-hidden="true" />

              {}
              <div className="absolute -top-6 left-1/2 -translate-x-1/2 font-mono text-[10px] text-muted-foreground tracking-widest" aria-hidden="true">
                + EST. 2024 +
              </div>

              <div className="relative overflow-hidden rounded-lg border border-border bg-card">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701222/img/perfume_bottle_mist.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701222/img/perfume_bottle_mist.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701222/img/perfume_bottle_mist.webp-wvc-sizes" alt="Elegant perfume bottle surrounded by delicate floral mist and soft lighting" loading="lazy" decoding="async" className="w-full h-auto object-cover aspect-[4/5]" />

                {}
                <div className="absolute top-4 left-4 right-4 flex justify-between items-start font-mono text-[10px] text-muted-foreground tracking-wider">
                  <div>
                    <div>SERIES: E-01</div>
                    <div className="mt-1">COLLECTION: PRIVATE</div>
                  </div>
                  <div className="text-right">
                    <div>EDP: 20%</div>
                    <div className="mt-1 text-primary">● PURE</div>
                  </div>
                </div>

                {}
                <div className="absolute top-1/2 right-4 -translate-y-1/2 bg-background/85 backdrop-blur-sm border border-border rounded-lg p-3 font-mono text-[11px] space-y-1 hidden sm:block">
                  <div className="text-muted-foreground tracking-wider">NOTES</div>
                  <div className="text-primary">OUD</div>
                  <div className="text-primary">ROSE</div>
                  <div className="text-muted-foreground">SANTAL ◌</div>
                </div>
              </div>

              {}
              <div className="absolute -bottom-6 -left-2 md:left-6 bg-card border border-border rounded-lg p-4 shadow-lg max-w-[260px]">
                <div className="flex items-center gap-2 mb-2">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full rounded-lg opacity-75" style={{
                    backgroundColor: "hsl(15 35% 65%)"
                  }} />
                    <span className="relative inline-flex rounded-lg h-2 w-2" style={{
                    backgroundColor: "hsl(15 35% 65%)"
                  }} />
                  </span>
                  <span className="font-mono text-[11px] tracking-wider" style={{
                  color: "hsl(15 35% 65%)"
                }}>
                    PROFILE COMPLETE — 100% MATCH
                  </span>
                </div>
                <div className="font-mono text-[10px] text-muted-foreground tracking-wider">
                  TS: 14:32:08 UTC
                </div>
              </div>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`transition-all duration-700 ease-out delay-150 ${rightVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
            {}
            <div className="flex items-center gap-2 mb-6">
              <span className="text-primary text-xs">●</span>
              <span className="font-mono text-xs tracking-[0.2em] text-primary uppercase">
                S/04 — DISCOVERY
              </span>
            </div>

            {}
            <h2 className="font-default font-bold uppercase leading-[0.95] tracking-tight text-5xl sm:text-6xl lg:text-7xl xl:text-8xl mb-6">
              THE OLFACTORY{" "}
              <span className="text-primary">QUIZ</span>
            </h2>

            {}
            <p className="text-lg md:text-xl text-muted-foreground mb-10 max-w-xl">
              Every scent tells a story. Let us help you find yours.
            </p>

            {}
            <div className="relative pl-6 border-l-2 border-primary space-y-6 mb-10">
              {features.map((feature, i) => {
              const Icon = feature.icon;
              return <div data-index={i} key={i} className="flex items-start gap-4">
                    <Icon className="w-4 h-4 mt-1 text-primary shrink-0" strokeWidth={1.5} />
                    <div>
                      <h3 className="font-default font-bold text-[15px] uppercase tracking-wider text-foreground mb-1">
                        {feature.title}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {feature.description}
                      </p>
                    </div>
                  </div>;
            })}
            </div>

            {}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button asChild size="lg" className="font-mono tracking-wider uppercase text-xs h-12 px-6 rounded-lg">
                <Link to="/ShopPage">
                  START QUIZ <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="font-mono tracking-wider uppercase text-xs h-12 px-6 rounded-lg border-border bg-transparent hover:bg-accent">
                <Link to="/OurHeritagePage">
                  OUR HERITAGE
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {}
        <div className="mt-20 flex justify-end">
          <p className="font-mono text-[10px] md:text-xs text-muted-foreground tracking-wider text-right">
            — EXCLUSIVE: HAND-BLENDED · SMALL BATCH · ARTISAN CRAFTED
          </p>
        </div>
      </div>
    </section>;
}